import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence, Reorder, useDragControls } from 'framer-motion';
import { ChevronRight, LogOut, GripVertical, Check, Pencil, PanelLeft, PanelRight, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';
import { CATEGORIES, TOOL_BY_KEY } from './toolRegistry';

// ── Ordering helpers ─────────────────────────────────────────────────
// Stable sort by an explicit key order; unlisted keys keep their default order.
export const applyOrder = (items, order, keyFn) => {
  const idx = new Map((order || []).map((k, i) => [k, i]));
  return items
    .map((item, i) => ({ item, i, rank: idx.has(keyFn(item)) ? idx.get(keyFn(item)) : Infinity }))
    .sort((a, b) => (a.rank - b.rank) || (a.i - b.i))
    .map((x) => x.item);
};

export const buildOrderedGroups = (allowed, groupOrder, toolOrder) => {
  const base = CATEGORIES
    .map((c) => ({ cat: c, items: c.tools ? [] : Object.values(TOOL_BY_KEY).filter((t) => t.category === c.id && allowed.has(t.key)) }))
    .filter((g) => g.items.length > 0);
  const ordered = applyOrder(base, groupOrder || [], (g) => g.cat.id);
  return ordered.map((g) => ({ cat: g.cat, items: applyOrder(g.items, (toolOrder || {})[g.cat.id] || [], (t) => t.key) }));
};

// ── Basic rows ───────────────────────────────────────────────────────
export const NavRow = ({ icon: Icon, label, active, onClick, testid, indent }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={cn(
      'group relative w-full flex items-center gap-3 rounded-xl text-[13.5px] transition-[background-color,color,transform] duration-200',
      indent ? 'pl-3 pr-2.5 py-[6px]' : 'px-2.5 py-[7px]',
      active
        ? 'bg-[#EEF3FF] text-[#1F47D6] font-semibold'
        : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900 font-medium',
    )}
  >
    {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r-full bg-[#3A6AFF]" />}
    {Icon && (
      <span
        className={cn(
          'flex items-center justify-center shrink-0 rounded-lg transition-colors duration-200',
          indent ? 'w-6 h-6' : 'w-7 h-7',
          active ? 'bg-white text-[#3A6AFF] shadow-[0_1px_2px_rgba(31,71,214,0.15)]' : 'text-slate-400 group-hover:text-slate-700',
        )}
      >
        <Icon className={indent ? 'w-[15px] h-[15px]' : 'w-[17px] h-[17px]'} strokeWidth={active ? 2.2 : 1.9} />
      </span>
    )}
    <span className="truncate flex-1 text-left group-hover:translate-x-[1px] transition-transform duration-200">{label}</span>
  </button>
);

export const SectionLabel = ({ children, icon }) => (
  <div className="px-2.5 mb-1.5 mt-6 flex items-center gap-1.5">
    {icon}
    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">{children}</span>
  </div>
);

// ── View-mode collapsible group ──────────────────────────────────────
const ToolGroup = ({ cat, items, isOpen, onToggle, onLaunch }) => (
  <div className="rounded-xl">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className={cn(
        'group w-full flex items-center gap-2 px-2.5 py-[7px] rounded-xl text-[10.5px] font-semibold uppercase tracking-[0.09em] transition-colors duration-200',
        isOpen ? 'text-slate-800' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100/70',
      )}
      data-testid={`sidebar-group-${cat.id}`}
    >
      <span className="w-5 h-5 flex items-center justify-center rounded-md text-slate-400 group-hover:text-slate-700 transition-colors">
        <ChevronRight className={cn('w-3.5 h-3.5 transition-transform duration-200', isOpen && 'rotate-90')} strokeWidth={2.2} />
      </span>
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: cat.dot }} />
      <span className="flex-1 text-left truncate">{cat.label}</span>
      <span className={cn('min-w-[20px] h-[18px] px-1.5 inline-flex items-center justify-center rounded-full text-[10px] font-semibold tabular-nums transition-colors', isOpen ? 'bg-[#EEF3FF] text-[#3A6AFF]' : 'bg-slate-100 text-slate-500')}>
        {items.length}
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="items"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          className="overflow-hidden"
        >
          <div className="ml-[19px] pl-2 my-1 border-l border-slate-200/90 space-y-[1px]">
            {items.map((t) => (
              <NavRow key={t.key} icon={t.icon} label={t.title} onClick={() => onLaunch(t)} testid={`sidebar-tool-${t.key}`} indent />
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ── Edit-mode draggable rows ─────────────────────────────────────────
const EditToolItem = ({ tool }) => (
  <Reorder.Item
    value={tool.key}
    style={{ touchAction: 'none' }}
    whileDrag={{ scale: 1.02, boxShadow: '0 6px 16px rgba(15,23,42,0.12)' }}
    className="flex items-center gap-2 pl-2 pr-2.5 py-[6px] rounded-lg bg-white cursor-grab active:cursor-grabbing select-none"
    data-testid={`sidebar-edit-tool-${tool.key}`}
  >
    <GripVertical className="w-3.5 h-3.5 text-slate-300 shrink-0" strokeWidth={2} />
    <span className="w-6 h-6 flex items-center justify-center rounded-lg text-slate-400 shrink-0">
      {tool.icon && <tool.icon className="w-[15px] h-[15px]" strokeWidth={1.9} />}
    </span>
    <span className="truncate flex-1 text-[13px] font-medium text-slate-700">{tool.title}</span>
  </Reorder.Item>
);

const EditGroupItem = ({ cat, toolKeys, onReorderTools }) => {
  const controls = useDragControls();
  const [open, setOpen] = useState(false);
  return (
    <Reorder.Item
      value={cat.id}
      dragListener={false}
      dragControls={controls}
      whileDrag={{ scale: 1.01, boxShadow: '0 8px 20px rgba(15,23,42,0.14)' }}
      className="rounded-xl bg-white border border-slate-200/70 mb-1.5 overflow-hidden"
      data-testid={`sidebar-edit-group-${cat.id}`}
    >
      <div className="flex items-center gap-2 px-2 py-[9px]">
        <button
          type="button"
          onPointerDown={(e) => controls.start(e)}
          className="shrink-0 cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"
          style={{ touchAction: 'none' }}
          aria-label={`Drag ${cat.label}`}
          data-testid={`sidebar-edit-group-handle-${cat.id}`}
        >
          <GripVertical className="w-4 h-4" strokeWidth={2} />
        </button>
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: cat.dot }} />
        <span className="flex-1 text-left truncate text-[11px] font-semibold uppercase tracking-[0.09em] text-slate-700">{cat.label}</span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Reorder tools"
          data-testid={`sidebar-edit-group-expand-${cat.id}`}
        >
          <ChevronRight className={cn('w-3.5 h-3.5 transition-transform duration-200', open && 'rotate-90')} strokeWidth={2.2} />
        </button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="tools"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <Reorder.Group axis="y" values={toolKeys} onReorder={(next) => onReorderTools(cat.id, next)} className="px-1.5 pb-2 pt-0.5 space-y-[2px]">
              {toolKeys.map((k) => TOOL_BY_KEY[k] && <EditToolItem key={k} tool={TOOL_BY_KEY[k]} />)}
            </Reorder.Group>
          </motion.div>
        )}
      </AnimatePresence>
    </Reorder.Item>
  );
};

// ── Reorderable workspace groups (shared by Classic & Smart layouts) ──
export const WorkspaceGroups = ({ orderedGroups, onLaunch, onReorder }) => {
  const [open, setOpen] = useState(() => new Set());
  const firstGroupId = orderedGroups[0]?.cat.id;
  useEffect(() => { if (firstGroupId) setOpen((prev) => (prev.size ? prev : new Set([firstGroupId]))); }, [firstGroupId]);
  const toggle = (id) => setOpen((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const [editing, setEditing] = useState(false);
  const [draftGroups, setDraftGroups] = useState([]);
  const [draftTools, setDraftTools] = useState({});
  const catById = useMemo(() => Object.fromEntries(CATEGORIES.map((c) => [c.id, c])), []);

  const enterEdit = () => {
    setDraftGroups(orderedGroups.map((g) => g.cat.id));
    setDraftTools(Object.fromEntries(orderedGroups.map((g) => [g.cat.id, g.items.map((t) => t.key)])));
    setEditing(true);
  };
  const reorderGroups = (ids) => { setDraftGroups(ids); onReorder?.(ids, draftTools); };
  const reorderTools = (catId, keys) => { const next = { ...draftTools, [catId]: keys }; setDraftTools(next); onReorder?.(draftGroups, next); };

  return (
    <>
      <div className="px-2.5 mb-1.5 mt-6 flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">Workspace</span>
        <button
          type="button"
          onClick={() => (editing ? setEditing(false) : enterEdit())}
          className={cn('inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10.5px] font-semibold transition-colors', editing ? 'bg-[#EEF3FF] text-[#1F47D6]' : 'text-slate-400 hover:text-slate-700 hover:bg-slate-100')}
          data-testid="sidebar-edit-toggle"
        >
          {editing ? <><Check className="w-3 h-3" strokeWidth={2.6} /> Done</> : <><Pencil className="w-3 h-3" strokeWidth={2.2} /> Edit</>}
        </button>
      </div>

      {editing ? (
        <>
          <p className="px-2.5 mb-2 text-[11px] leading-4 text-slate-400" data-testid="sidebar-edit-hint">
            Drag <GripVertical className="inline w-3 h-3 -mt-0.5" /> to reorder sections. Tap the arrow to reorder the tools inside.
          </p>
          <Reorder.Group axis="y" values={draftGroups} onReorder={reorderGroups}>
            {draftGroups.map((id) => catById[id] && (
              <EditGroupItem key={id} cat={catById[id]} toolKeys={draftTools[id] || []} onReorderTools={reorderTools} />
            ))}
          </Reorder.Group>
        </>
      ) : (
        <div className="space-y-[2px]">
          {orderedGroups.map(({ cat, items }) => (
            <ToolGroup key={cat.id} cat={cat} items={items} isOpen={open.has(cat.id)} onToggle={() => toggle(cat.id)} onLaunch={onLaunch} />
          ))}
        </div>
      )}
    </>
  );
};

// ── Layout picker button (choose Classic / Icon Rail / Smart) ─────────
const LAYOUTS = [
  { key: 'refined', label: 'Classic', desc: 'Full list with sections', icon: PanelLeft },
  { key: 'rail', label: 'Icon Rail', desc: 'Slim icons + fly-out', icon: PanelRight },
  { key: 'adaptive', label: 'Smart', desc: 'AI-suggested + your order', icon: Sparkles },
];

export const LayoutPicker = ({ layout, onChange, compact = false }) => {
  const [open, setOpen] = useState(false);
  const current = LAYOUTS.find((l) => l.key === layout) || LAYOUTS[0];
  const Trigger = current.icon;
  return (
    <div className={cn('relative', compact ? '' : 'px-3')}>
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} data-testid="layout-picker-backdrop" />}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="sidebar-layout-picker"
        className={cn(
          'relative z-40 flex items-center rounded-xl border border-slate-200/80 bg-white text-slate-600 hover:text-slate-900 hover:shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-all duration-200',
          compact ? 'w-11 h-11 justify-center' : 'w-full gap-2.5 px-2.5 py-2',
        )}
        title="Change sidebar layout"
      >
        <Trigger className="w-[18px] h-[18px] shrink-0 text-[#3A6AFF]" strokeWidth={2} />
        {!compact && (
          <>
            <span className="flex-1 text-left text-[12.5px] font-semibold truncate">{current.label} layout</span>
            <ChevronRight className={cn('w-3.5 h-3.5 text-slate-400 transition-transform', open && 'rotate-90')} strokeWidth={2.2} />
          </>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className={cn('absolute z-50 bottom-full mb-2 w-60 rounded-2xl border border-slate-200/90 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.16)] p-1.5', compact ? 'left-0' : 'left-3 right-3 w-auto')}
            data-testid="layout-picker-menu"
          >
            <p className="px-2.5 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Sidebar layout</p>
            {LAYOUTS.map((l) => {
              const Icon = l.icon;
              const active = l.key === layout;
              return (
                <button
                  key={l.key}
                  type="button"
                  onClick={() => { onChange?.(l.key); setOpen(false); }}
                  data-testid={`layout-option-${l.key}`}
                  className={cn('w-full flex items-start gap-2.5 px-2.5 py-2 rounded-xl text-left transition-colors', active ? 'bg-[#EEF3FF]' : 'hover:bg-slate-50')}
                >
                  <span className={cn('mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0', active ? 'bg-white text-[#3A6AFF] shadow-[0_1px_2px_rgba(31,71,214,0.15)]' : 'bg-slate-100 text-slate-500')}>
                    <Icon className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-[13px] font-semibold leading-tight', active ? 'text-[#1F47D6]' : 'text-slate-800')}>{l.label}</span>
                    <span className="block text-[11px] text-slate-400 leading-tight mt-0.5">{l.desc}</span>
                  </span>
                  {active && <Check className="w-4 h-4 text-[#3A6AFF] shrink-0 mt-1" strokeWidth={2.6} />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ── User block ───────────────────────────────────────────────────────
export const UserBlock = ({ user, onLogout, compact = false }) => {
  const initials = (user?.name || 'U').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const roleLabel = (user?.role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const Avatar = user?.avatar_url ? (
    <img src={user.avatar_url} alt={user?.name || 'User'} className="w-9 h-9 rounded-full object-cover ring-2 ring-white shadow-sm" />
  ) : (
    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#5A8BFF] to-[#1F47D6] ring-2 ring-white shadow-sm flex items-center justify-center text-[12px] font-bold text-white">{initials}</div>
  );

  if (compact) {
    return (
      <div className="p-2.5 border-t border-slate-200/70 flex flex-col items-center gap-2" data-testid="sidebar-user">
        <div className="relative">{Avatar}<span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white" /></div>
        <button type="button" onClick={onLogout} className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" data-testid="sidebar-logout" aria-label="Log out" title="Log out">
          <LogOut className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <div className="p-3 border-t border-slate-200/70">
      <div className="flex items-center gap-3 rounded-xl border border-slate-200/80 bg-slate-50/60 px-2.5 py-2 hover:bg-white hover:shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-[background-color,box-shadow] duration-200" data-testid="sidebar-user">
        <div className="relative shrink-0">{Avatar}<span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 ring-2 ring-white" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-slate-900 truncate leading-tight">{user?.name}</p>
          <p className="text-[11px] text-slate-400 truncate leading-tight mt-0.5">{roleLabel}</p>
        </div>
        <button type="button" onClick={onLogout} className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors duration-200" data-testid="sidebar-logout" aria-label="Log out" title="Log out">
          <LogOut className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};
